import { useLocation } from 'react-router-dom';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import { useAuth } from 'react-oidc-context';
import getMenuItems from './menuItems';
import React, { useMemo } from 'react';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import Styles from './Styles';
import useClientTheme from '#/common/hooks/useClientTheme';
import { isUUID } from '#/common/utils/patterns/uuid';
import useSession from '#/common/hooks/useSession';
import { UserRole } from '#/common/types/userTypes';
import DesktopMenu from './DesktopMenu';
import MobileMenu from './MobileMenu';
import { getActiveMenu } from './util';
import { COGNITO } from '#/config/consts';
import { useMenuCollapse } from './MenuCollapseContext';

const logoutRedirect = () => {
  const url = new URL(COGNITO.domain);
  url.pathname = '/logout';
  url.searchParams.set('client_id', COGNITO.clientId);
  url.searchParams.set('redirect_uri', COGNITO.returnUrl);
  url.searchParams.set('response_type', COGNITO.responseType);
  window.location.href = url.toString();
};

const clearOidcKeys = () => {
  const clearKeysWithPrefix = (storage: Storage, prefix: string) => {
    Object.keys(storage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => storage.removeItem(key));
  };

  clearKeysWithPrefix(localStorage, 'oidc');
  clearKeysWithPrefix(sessionStorage, 'oidc');
};

// !Important: Logout on localhost will not work with the current setup
const handleLogout = () => {
  console.debug('[OIDC] logout → clearing user + Apollo cache');

  try {
    logoutRedirect();
    clearOidcKeys();
    console.debug('[Logout] cleanup done');
  } catch (e) {
    console.warn('[OIDC] cleanup failed', e);
  }
};

export const RoleToRoleNameMap: Record<UserRole, string> = {
  tmm_admin: 'Admin',
  tmm_manager: 'Facility Manager',
  tmm_technician: 'Technician',
};

/* ---------------------- Root switcher ---------------------- */
const AppMenu: React.FC = () => {
  const { isDesktop } = useViewport();
  const { logo } = useClientTheme();
  const { isCollapsed, toggleCollapse } = useMenuCollapse();

  // get full authentication object, not only "isAuthenticated"
  const auth = useAuth();
  const { isAuthenticated } = auth;

  const location = useLocation();

  const { user, tenants } = useSession();
  const role = user?.role as UserRole | undefined;

  const menuItems = getMenuItems({ role });

  const userRole = (user?.role ?? '') as UserRole | undefined;

  const { index: selectedIndex, route: activeRoute } = getActiveMenu(location.pathname, menuItems);

  const { pathname } = useLocation();

  const shouldShowForMobile = useMemo(() => {
    const pathnameSplit = pathname.split('/');

    return !pathnameSplit.some((el) => el === 'new' || isUUID(el));
  }, [pathname]);

  if (!shouldShowForMobile && !isDesktop) return null;

  return (
    <Styles isDesktop={isDesktop} isCollapsed={isDesktop && isCollapsed} data-menu-container>
      {isAuthenticated && (
        <NuvoBox>
          {isDesktop ? (
            <DesktopMenu
              logo={logo}
              userName={user?.name}
              userRole={userRole}
              selectedIndex={selectedIndex}
              onLogout={handleLogout}
              isCollapsed={isCollapsed}
              onToggleCollapse={toggleCollapse}
            />
          ) : (
            <MobileMenu
              userName={user?.name}
              userRole={userRole}
              activeRoute={activeRoute}
              onLogout={handleLogout}
            />
          )}
        </NuvoBox>
      )}
    </Styles>
  );
};

export default React.memo(AppMenu);
